// src/App.tsx
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import Home from "./Components/HomePageComponents/Home";
// Login and Signup are now rendered as modals on the homepage.
// Direct route visits redirect to /?modal=login or /?modal=signup.
import Logout from "./Components/UserAuthComponents/Logout";
import VerifyEmail from "./Components/UserAuthComponents/VerifyEmail";
import ResetPassword from "./Components/UserAuthComponents/ResetPassword";
import Dashboard from "./Components/DashboardPageComponents/Dashboard";
import DocumentStatusPage from "./Components/Pages/DocumentStatusPage";
import TemplatesPage from "./Components/Pages/TemplatesPage";
import ProfilePage from "./Components/Pages/ProfilePage";
import { UserProvider, useUser } from "./Context/UserContext";
import { SendFlowProvider } from "./Context/SendFlowContext";
import DocumentStatusDetailPage from "./Components/Pages/DocumentStatusDetailPage";
import ErrorBoundary from "./Components/Partials/ErrorBoundary";
import NotFoundPage from "./Components/Pages/NotFoundPage";
import TermsPage from "./Components/Pages/TermsPage";
import PrivacyPage from "./Components/Pages/PrivacyPage";

// Lazy-loaded heavy route components
const SendDocumentPage = React.lazy(() => import("./Components/Pages/SendDocumentPage"));
const EditTemplate = React.lazy(() => import("./Components/Pages/EditTemplate"));
const QuickSendPage = React.lazy(() => import("./Components/Pages/QuickSendPage"));
const EnvelopeOwnerPage = React.lazy(() => import("./Components/Envelope/EnvelopeOwnerPage"));
const SignEnvelopePage = React.lazy(() => import("./Components/Envelope/SignEnvelopePage"));

/** Simple full-viewport splash to avoid flicker during initial auth load */
function Splash() {
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 16,
        color: "#444",
      }}
    >
      Loading…
    </div>
  );
}

/** Public-only wrapper. While loading, show Splash (prevents flicker) */
function PublicOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useUser();
  if (loading) return <Splash />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Auth-only wrapper. While loading, show Splash (prevents flicker) */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useUser();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Index route that NEVER shows Home for logged-in users */
function IndexDecider() {
  const { user, loading } = useUser();
  if (loading) return <Splash />;
  return user ? <Navigate to="/dashboard" replace /> : <Home />;
}

function AppRoutes() {
  return (
    <React.Suspense fallback={<Splash />}>
      <Routes>
        {/* Index: hard-decide between Home vs Dashboard */}
        <Route path="/" element={<IndexDecider />} />

        {/* Public pages (no auth required) */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Public pages visible only when logged OUT */}
        <Route
          path="/login"
          element={
            <PublicOnly>
              <Navigate to="/?modal=login" replace />
            </PublicOnly>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicOnly>
              <Navigate to="/?modal=signup" replace />
            </PublicOnly>
          }
        />
        <Route
          path="/reset-password/:token"
          element={
            <PublicOnly>
              <ResetPassword />
            </PublicOnly>
          }
        />
        <Route
          path="/verify-email"
          element={
            <PublicOnly>
              <VerifyEmail />
            </PublicOnly>
          }
        />

        {/* Protected pages visible only when logged IN */}
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <Dashboard />
            </RequireAuth>
          }
        />
        <Route
          path="/document-status"
          element={
            <RequireAuth>
              <DocumentStatusPage />
            </RequireAuth>
          }
        />
        <Route
          path="/document-status/:contractURL"
          element={
            <RequireAuth>
              <DocumentStatusDetailPage />
            </RequireAuth>
          }
        />
        <Route
          path="/templates"
          element={
            <RequireAuth>
              <TemplatesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/template/:templateURL"
          element={
            <RequireAuth>
              <EditTemplate />
            </RequireAuth>
          }
        />
        <Route
          path="/quick-send"
          element={
            <RequireAuth>
              <QuickSendPage />
            </RequireAuth>
          }
        />
        <Route
          path="/send"
          element={
            <RequireAuth>
              <SendDocumentPage />
            </RequireAuth>
          }
        />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          }
        />

        {/* OWNER VIEW – must be logged in (verifyAccess on backend) */}
        <Route
          path="/envelope/:token"
          element={
            <RequireAuth>
              <EnvelopeOwnerPage />
            </RequireAuth>
          }
        />

        {/* SIGNER VIEW – public (no verifyAccess on backend) */}
        <Route path="/sign/:token" element={<SignEnvelopePage />} />

        {/* Logout can be hit in either state */}
        <Route path="/logout" element={<Logout />} />

        {/* Fallback — 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </React.Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <UserProvider>
        <SendFlowProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </BrowserRouter>
        </SendFlowProvider>
      </UserProvider>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

export default App;
